/**
 * Report Export Utility — Clinical-grade PDF generation and printing.
 *
 * Uses jsPDF directly for deterministic client-side PDF rendering.
 * Builds a complete clinical document with header, structured sections,
 * and footer — suitable for EMR/PACS upload.
 */
import { jsPDF } from 'jspdf';
import type { ReportResponse } from '../services/reportService';
import { getClinicBranding, type ClinicBrandingConfig } from '../config/clinicBranding';
import { formatReportContentHtml } from './reportUtils';

// ── Types ──────────────────────────────────────────────────────────

interface ReportSection {
  title: string;
  content: string;
}

export interface PatientContext {
  first_name?: string;
  last_name?: string;
  mrn?: string;
  dob?: string;
  gender?: string;
  accession_number?: string;
  modality?: string;
  study_date?: string;
  body_part?: string;
}

export interface ExportOptions {
  branding?: ClinicBrandingConfig;
  radiologistName?: string;
  filename?: string;
  patientContext?: PatientContext;
  exportTheme?: string;
}

// ── Theme palettes (mirrors backend _HTML_THEMES / _THEMES) ────────

interface PdfTheme {
  headingRgb: [number, number, number];
  accentRgb: [number, number, number];
  headerBorderCss: string;
  patientBgCss: string;
  patientBorderCss: string;
  patientLabelCss: string;
  sectionTitleCss: string;
}

const PDF_THEMES: Record<string, PdfTheme> = {
  blue: {
    headingRgb: [21, 101, 192],
    accentRgb: [25, 118, 210],
    headerBorderCss: '#1976d2',
    patientBgCss: '#f5f9ff',
    patientBorderCss: '#e3eaf4',
    patientLabelCss: '#1565c0',
    sectionTitleCss: '#1565c0',
  },
  black: {
    headingRgb: [0, 0, 0],
    accentRgb: [51, 51, 51],
    headerBorderCss: '#333333',
    patientBgCss: '#f7f7f7',
    patientBorderCss: '#d0d0d0',
    patientLabelCss: '#222222',
    sectionTitleCss: '#000000',
  },
  hospital: {
    headingRgb: [0, 87, 135],
    accentRgb: [0, 122, 184],
    headerBorderCss: '#007ab8',
    patientBgCss: '#f0f6fa',
    patientBorderCss: '#c2dce8',
    patientLabelCss: '#005787',
    sectionTitleCss: '#005787',
  },
  grayscale: {
    headingRgb: [43, 43, 43],
    accentRgb: [85, 85, 85],
    headerBorderCss: '#555555',
    patientBgCss: '#f5f5f5',
    patientBorderCss: '#d5d5d5',
    patientLabelCss: '#333333',
    sectionTitleCss: '#2b2b2b',
  },
};

function resolvePdfTheme(name?: string): PdfTheme {
  return PDF_THEMES[(name || 'blue').toLowerCase()] ?? PDF_THEMES['blue'];
}

// ── Section Parsing ────────────────────────────────────────────────

// Known section headers that should be treated as section titles (lowercase for matching)
const KNOWN_SECTION_HEADERS = new Set([
  'exam',
  'examination',
  'clinical data',
  'clinical history',
  'clinical indication',
  'technique',
  'findings',
  'impression',
  'recommendation',
  'recommendations',
  'comparison',
  'prior studies',
  'prior',
  'limitations',
  'limitation',
  'indication',
  'history',
  'reason for exam',
  'procedure',
  'protocol',
  'conclusion',
  'notes',
  'patient history summary',
  'patient history',
  'patient information',
]);

function parseReportSections(reportText: string): ReportSection[] {
  const lines = reportText.split('\n');
  const sections: ReportSection[] = [];
  let currentTitle = '';
  let currentContent: string[] = [];

  for (const line of lines) {
    const trimmed = line.trim();

    // Only KNOWN section headers create a section break.
    // Sub-headers like "Brain Parenchyma:" or "Diffusion and Susceptibility:"
    // must remain inside the current section so the subsection parser can
    // bold them consistently. Arbitrary Title Case phrases must NOT become
    // new sections — that breaks WYSIWYG and causes FINDINGS to disappear.
    let matchedHeader = '';
    let inlineContent = '';
    const colonIdx = trimmed.indexOf(':');
    if (colonIdx >= 3) {
      const candidate = trimmed.slice(0, colonIdx).trim();
      const afterColon = trimmed.slice(colonIdx + 1).trim();
      const isKnown = KNOWN_SECTION_HEADERS.has(candidate.toLowerCase());
      if (isKnown) {
        matchedHeader = candidate.toUpperCase();
        inlineContent = afterColon;
      }
    }

    if (matchedHeader) {
      // Save previous section (always save, even if content is empty,
      // so sections like FINDINGS with only blank lines are preserved)
      if (currentTitle) {
        sections.push({ title: currentTitle, content: currentContent.join('\n').trim() });
      }
      currentTitle = matchedHeader;
      currentContent = inlineContent ? [inlineContent] : [];
    } else {
      currentContent.push(line);
    }
  }

  // Save final section
  if (currentTitle) {
    sections.push({ title: currentTitle, content: currentContent.join('\n').trim() });
  }

  // Fallback if no sections found
  if (sections.length === 0 && reportText.trim()) {
    sections.push({ title: 'Report', content: reportText.trim() });
  }

  return sections;
}

// ── PDF Layout Constants (mm) ──────────────────────────────────────

const PAGE = {
  width: 210,     // A4
  height: 297,
  marginTop: 18,
  marginBottom: 18,
  marginLeft: 16,
  marginRight: 16,
};

const CONTENT_WIDTH = PAGE.width - PAGE.marginLeft - PAGE.marginRight;

// ── jsPDF Document Builder ─────────────────────────────────────────

function addPageIfNeeded(doc: jsPDF, y: number, needed: number): number {
  if (y + needed > PAGE.height - PAGE.marginBottom) {
    doc.addPage();
    return PAGE.marginTop;
  }
  return y;
}

/**
 * Render a text line that may contain **bold** segments using jsPDF.
 * Splits the text by **bold** markers and renders alternating bold/normal runs.
 */
function renderMixedBoldLine(
  doc: jsPDF,
  text: string,
  startX: number,
  y: number,
  fontSize: number,
): void {
  doc.setFontSize(fontSize);
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  let x = startX;

  for (const part of parts) {
    const boldMatch = part.match(/^\*\*([^*]+)\*\*$/);
    const isBold = !!boldMatch;
    const displayText = boldMatch ? boldMatch[1] : part;
    if (!displayText) continue;

    doc.setFont('helvetica', isBold ? 'bold' : 'normal');
    doc.setTextColor(34, 34, 34);
    doc.text(displayText, x, y);
    x += doc.getTextWidth(displayText);
  }
}

function buildPdfDocument(
  report: ReportResponse,
  sections: ReportSection[],
  branding: ClinicBrandingConfig,
  radiologistName?: string,
  options?: ExportOptions,
): jsPDF {
  const theme = resolvePdfTheme(options?.exportTheme);
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  let y = PAGE.marginTop;
  const x = PAGE.marginLeft;

  // ── Header: Clinic name ──
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(...theme.accentRgb);
  doc.text(branding.clinicName, x, y);
  y += 6;

  // ── Header: Address / phone ──
  if (branding.address || branding.phone) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(102, 102, 102);
    if (branding.address) {
      doc.text(branding.address, x, y);
      y += 3.5;
    }
    if (branding.phone) {
      doc.text(branding.phone, x, y);
      y += 3.5;
    }
  }

  // ── Status badge ──
  const statusText = report.status === 'final' ? 'FINALIZED' : (report.status || 'DRAFT').toUpperCase();
  const badgeColor: [number, number, number] = report.status === 'final' ? [46, 125, 50] : [230, 81, 0];
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...badgeColor);
  doc.text(statusText, PAGE.width - PAGE.marginRight, PAGE.marginTop, { align: 'right' });

  if (report.status === 'final' && report.finalized_at) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(102, 102, 102);
    doc.text(
      new Date(report.finalized_at).toLocaleString(),
      PAGE.width - PAGE.marginRight,
      PAGE.marginTop + 4,
      { align: 'right' },
    );
  }

  y += 2;

  // ── Header divider ──
  doc.setDrawColor(...theme.accentRgb);
  doc.setLineWidth(0.5);
  doc.line(x, y, PAGE.width - PAGE.marginRight, y);
  y += 5;

  // ── Patient details (if available) ──
  const patient = options?.patientContext;
  if (patient && (patient.first_name || patient.last_name || patient.mrn)) {
    const patientName = [patient.first_name, patient.last_name].filter(Boolean).join(' ') || '—';
    const formatDob = (dob?: string) => {
      if (!dob) return '—';
      try { return new Date(dob).toLocaleDateString(); } catch { return dob; }
    };
    const genderLabel = patient.gender === 'M' ? 'Male' : patient.gender === 'F' ? 'Female' : (patient.gender || '—');

    const patientMeta: [string, string][] = [
      ['Patient Name', patientName],
      ['MRN', patient.mrn || '—'],
      ['Date of Birth', formatDob(patient.dob)],
      ['Gender', genderLabel],
    ];
    if (patient.accession_number) patientMeta.push(['Accession #', patient.accession_number]);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(34, 34, 34);
    doc.text('PATIENT INFORMATION', x, y);
    y += 1;
    doc.setDrawColor(224, 224, 224);
    doc.setLineWidth(0.2);
    doc.line(x, y, PAGE.width - PAGE.marginRight, y);
    y += 4;

    doc.setFontSize(8);
    for (const [label, value] of patientMeta) {
      y = addPageIfNeeded(doc, y, 5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(85, 85, 85);
      doc.text(`${label}:`, x, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(34, 34, 34);
      doc.text(value, x + 28, y);
      y += 4;
    }
    y += 4;
  }

  // ── Metadata table ──
  const cptDisplay = report.cpt_code
    ? `${report.cpt_code} — ${report.cpt_exam_name || ''}`
    : '—';
  const meta: [string, string][] = [
    ['CPT Code', cptDisplay],
    ['Radiologist', radiologistName || '—'],
    ['Created', new Date(report.created_at).toLocaleString()],
    ['Report ID', String(report.id)],
  ];

  doc.setFontSize(8);
  for (const [label, value] of meta) {
    y = addPageIfNeeded(doc, y, 5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(85, 85, 85);
    doc.text(`${label}:`, x, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(34, 34, 34);
    doc.text(value, x + 28, y);
    y += 4;
  }

  y += 4;

  // ── Report sections ──
  for (const section of sections) {
    // Section title
    y = addPageIfNeeded(doc, y, 14);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...theme.headingRgb);
    doc.text(section.title, x, y);
    y += 1;

    // Title underline
    doc.setDrawColor(224, 224, 224);
    doc.setLineWidth(0.2);
    doc.line(x, y, PAGE.width - PAGE.marginRight, y);
    y += 4;

    // Section content — detect sub-headers (markdown bold, ALL-CAPS, Title Case)
    // and render headers in bold font to match the on-screen preview.
    const contentLines = section.content.split('\n');
    const lineHeight = 4.2;

    /**
     * Try to extract a bold header + body from a line using the same rules as
     * the unified frontend parser (reportUtils.ts parseReportLine).
     */
    const tryExtractHeader = (text: string): { header: string; rest: string } | null => {
      const trimmed = text.trim();
      if (!trimmed) return null;

      // 1. Markdown bold: **Header:** or **Header:** rest
      // Only treated as a structural header if the bold span is short (≤60 chars)
      // and ends with ':'. Long bold spans (inline findings) are text content.
      const mdBold = trimmed.match(/^\*\*([^*]+?)\*\*(.*)$/);
      if (mdBold) {
        const boldText = mdBold[1];
        const rest = mdBold[2].trimStart();
        if (boldText.endsWith(':') && boldText.length <= 60) {
          return { header: boldText, rest };
        }
        // Long bold span or no colon — treat as plain text (renderInlineBold handles it)
        return null;
      }

      // 2. ALL-CAPS: FINDINGS:, CLINICAL HISTORY:
      const allCaps = trimmed.match(/^([A-Z][A-Z\s/&()\u002D]{1,40}:)(.*)$/);
      if (allCaps) {
        return { header: allCaps[1], rest: allCaps[2].trimStart() };
      }

      // 3. Title Case: Brain Parenchyma:, Diffusion and Susceptibility:
      const titleCase = trimmed.match(/^([A-Z][A-Za-z\s,/&()\u002D]{2,60}:)(.*)$/);
      if (titleCase && !titleCase[1].includes('.')) {
        return { header: titleCase[1], rest: titleCase[2].trimStart() };
      }

      return null;
    };

    for (const rawLine of contentLines) {
      const line = rawLine.trim();
      if (!line) {
        y += lineHeight;
        continue;
      }

      const extracted = tryExtractHeader(rawLine);
      if (extracted) {
        y = addPageIfNeeded(doc, y, lineHeight + 1);

        // Render bold header
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9.5);
        doc.setTextColor(34, 34, 34);
        doc.text(extracted.header, x, y);
        const headerWidth = doc.getTextWidth(extracted.header + ' ');

        // Render rest if any in normal font, with proper wrapping
        if (extracted.rest) {
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(34, 34, 34);
          const remainingWidth = CONTENT_WIDTH - headerWidth;
          const wrappedRest = doc.splitTextToSize(extracted.rest, remainingWidth);
          // First line on same row as header
          doc.text(wrappedRest[0], x + headerWidth, y);
          // Remaining lines wrap to full width on next rows
          for (let i = 1; i < wrappedRest.length; i++) {
            y += lineHeight;
            y = addPageIfNeeded(doc, y, lineHeight + 1);
            const continuationLines = doc.splitTextToSize(wrappedRest[i], CONTENT_WIDTH);
            doc.text(continuationLines[0], x, y);
          }
        }
        y += lineHeight;
      } else {
        // Plain text line — render inline **bold** spans and wrap to content width
        doc.setFontSize(9.5);
        doc.setTextColor(34, 34, 34);

        // Split by **bold** markers
        const parts = line.split(/(\*\*[^*]+\*\*)/g);
        const words: { text: string; bold: boolean }[] = [];

        // Flatten into word-level bold flags — split bold spans by whitespace
        // so long **bold sentences** wrap correctly at word boundaries
        for (const part of parts) {
          const boldMatch = part.match(/^\*\*([^*]+)\*\*$/);
          const isBold = !!boldMatch;
          const text = boldMatch ? boldMatch[1] : part;
          const partWords = text.trim().split(/\s+/).filter(w => w);
          for (const w of partWords) {
            words.push({ text: w, bold: isBold });
          }
        }

        // Render words with wrapping — keep **bold** markers in buffer
        let lineBuffer = '';
        for (const word of words) {
          const rawWord = word.bold ? `**${word.text}**` : word.text;
          const testText = lineBuffer ? `${lineBuffer} ${rawWord}` : rawWord;
          // Use stripped text for width measurement (jsPDF doesn't know **)
          const strippedText = testText.replace(/\*\*([^*]+)\*\*/g, '$1');
          const testWidth = doc.getTextWidth(strippedText);
          if (testWidth <= CONTENT_WIDTH) {
            lineBuffer = testText;
          } else {
            // Flush current line
            if (lineBuffer) {
              y = addPageIfNeeded(doc, y, lineHeight + 1);
              renderMixedBoldLine(doc, lineBuffer, x, y, 9.5);
              y += lineHeight;
            }
            lineBuffer = rawWord;
          }
        }
        // Flush last line
        if (lineBuffer) {
          y = addPageIfNeeded(doc, y, lineHeight + 1);
          renderMixedBoldLine(doc, lineBuffer, x, y, 9.5);
          y += lineHeight;
        }
      }
    }

    y += 6; // Gap between sections
  }

  // ── Footer ──
  y = addPageIfNeeded(doc, y, 12);
  y += 4;
  doc.setDrawColor(204, 204, 204);
  doc.setLineWidth(0.2);
  doc.line(x, y, PAGE.width - PAGE.marginRight, y);
  y += 4;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(153, 153, 153);
  doc.text(branding.footer, PAGE.width / 2, y, { align: 'center' });

  console.log('PDF_DOCUMENT_BUILT | pages=%d sections=%d', doc.getNumberOfPages(), sections.length);
  return doc;
}

// ── HTML Document Builder (for print only) ─────────────────────────

function buildPatientHtml(options?: ExportOptions): string {
  const p = options?.patientContext;
  if (!p || (!p.first_name && !p.last_name && !p.mrn)) return '';
  const name = [p.first_name, p.last_name].filter(Boolean).join(' ') || '—';
  const formatDob = (dob?: string) => {
    if (!dob) return '—';
    try { return new Date(dob).toLocaleDateString(); } catch { return dob; }
  };
  const gender = p.gender === 'M' ? 'Male' : p.gender === 'F' ? 'Female' : (p.gender || '—');
  let rows = `
    <tr>
      <td style="font-weight:600;color:#555;padding:3px 6px 3px 0;width:14%;">Patient</td>
      <td style="color:#222;padding:3px 14px 3px 0;width:36%;">${escapeHtml(name)}</td>
      <td style="font-weight:600;color:#555;padding:3px 6px 3px 0;width:14%;">MRN</td>
      <td style="color:#222;padding:3px 0;">${escapeHtml(p.mrn || '—')}</td>
    </tr>
    <tr>
      <td style="font-weight:600;color:#555;padding:3px 6px 3px 0;">Date of Birth</td>
      <td style="color:#222;padding:3px 14px 3px 0;">${escapeHtml(formatDob(p.dob))}</td>
      <td style="font-weight:600;color:#555;padding:3px 6px 3px 0;">Gender</td>
      <td style="color:#222;padding:3px 0;">${escapeHtml(gender)}</td>
    </tr>`;
  if (p.accession_number || p.study_date) {
    const studyDate = p.study_date ? (() => { try { return new Date(p.study_date || '').toLocaleDateString(); } catch { return p.study_date || '—'; } })() : '—';
    rows += `
    <tr>
      <td style="font-weight:600;color:#555;padding:3px 6px 3px 0;">Accession #</td>
      <td style="color:#222;padding:3px 14px 3px 0;">${escapeHtml(p.accession_number || '—')}</td>
      <td style="font-weight:600;color:#555;padding:3px 6px 3px 0;">Study Date</td>
      <td style="color:#222;padding:3px 0;">${escapeHtml(studyDate)}</td>
    </tr>`;
  }
  return `
    <div style="margin-bottom:12px;padding:10px 12px;background:#f5f9ff;border:1px solid #e3eaf4;border-radius:6px;">
      <div style="font-size:10px;font-weight:700;color:#1565c0;text-transform:uppercase;letter-spacing:0.8px;margin-bottom:6px;">Patient Information</div>
      <table style="width:100%;border-collapse:collapse;font-size:11px;">
        ${rows}
      </table>
    </div>`;
}

function buildExportHTML(
  report: ReportResponse,
  sections: ReportSection[],
  branding: ClinicBrandingConfig,
  radiologistName?: string,
  options?: ExportOptions,
): string {
  const theme = resolvePdfTheme(options?.exportTheme);
  const finalizedDate = report.finalized_at
    ? new Date(report.finalized_at).toLocaleString()
    : '—';
  const createdDate = new Date(report.created_at).toLocaleString();

  const logoHtml = branding.logoUrl
    ? `<img src="${branding.logoUrl}" alt="${branding.clinicName}" style="height:36px;margin-bottom:4px;display:block;" />`
    : '';

  const addressHtml = branding.address
    ? `<div style="font-size:10px;color:#666;margin-top:2px;">${branding.address}</div>`
    : '';

  const phoneHtml = branding.phone
    ? `<div style="font-size:10px;color:#666;">${branding.phone}</div>`
    : '';

  // Use shared formatter so clipboard, editor preview, and HTML export all
  // produce identical sub-header bold rendering.
  const sectionContentHtml = (content: string): string => formatReportContentHtml(content);

  const sectionsHtml = sections
    .map(
      (s) => `
      <div class="report-section" style="margin-bottom:18px;page-break-inside:avoid;">
        <div style="font-size:13px;font-weight:700;color:${theme.sectionTitleCss};text-transform:uppercase;letter-spacing:0.8px;margin-bottom:6px;padding-bottom:4px;border-bottom:1px solid #e0e0e0;">
          ${escapeHtml(s.title)}
        </div>
        <div style="font-size:13px;line-height:1.65;color:#222;">
          ${sectionContentHtml(s.content)}
        </div>
      </div>
    `,
    )
    .join('');

  return `
    <div id="report-export-container" style="font-family:'Inter','Roboto','Helvetica Neue',Arial,sans-serif;color:#222;max-width:800px;margin:0 auto;padding:0;">
      <div style="padding:0 0 14px 0;border-bottom:2px solid ${theme.headerBorderCss};margin-bottom:18px;">
        <div style="display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:10px;">
          <div>
            ${logoHtml}
            <div style="font-size:17px;font-weight:700;color:${theme.sectionTitleCss};letter-spacing:0.5px;">${escapeHtml(branding.clinicName)}</div>
            ${addressHtml}
            ${phoneHtml}
          </div>
          <div style="padding:5px 14px;border-radius:4px;background:${report.status === 'final' ? '#e8f5e9' : '#fff3e0'};border:1px solid ${report.status === 'final' ? '#4caf50' : '#ff9800'};text-align:center;">
            <div style="font-size:11px;font-weight:700;color:${report.status === 'final' ? '#2e7d32' : '#e65100'};text-transform:uppercase;letter-spacing:1px;">
              ${report.status === 'final' ? '✓ FINALIZED' : (report.status || 'DRAFT').toUpperCase()}
            </div>
            ${report.status === 'final' ? `<div style="font-size:9px;color:#666;margin-top:2px;">${finalizedDate}</div>` : ''}
          </div>
        </div>
        ${buildPatientHtml(options)}
        <table style="width:100%;border-collapse:collapse;font-size:11px;margin-top:6px;">
          <tr>
            <td style="font-weight:600;color:#555;padding:3px 6px 3px 0;width:14%;">CPT Code</td>
            <td style="color:#222;padding:3px 14px 3px 0;width:36%;">${report.cpt_code ? escapeHtml(`${report.cpt_code} — ${report.cpt_exam_name || ''}`) : '—'}</td>
            <td style="font-weight:600;color:#555;padding:3px 6px 3px 0;width:14%;">Radiologist</td>
            <td style="color:#222;padding:3px 0;">${escapeHtml(radiologistName || '—')}</td>
          </tr>
          <tr>
            <td style="font-weight:600;color:#555;padding:3px 6px 3px 0;">Created</td>
            <td style="color:#222;padding:3px 14px 3px 0;">${createdDate}</td>
            <td style="font-weight:600;color:#555;padding:3px 6px 3px 0;">Report ID</td>
            <td style="color:#222;padding:3px 0;">${report.id}</td>
          </tr>
        </table>
      </div>
      ${sectionsHtml}
      <div style="margin-top:28px;padding-top:10px;border-top:1px solid #ccc;font-size:9px;color:#999;text-align:center;">
        ${escapeHtml(branding.footer)}
      </div>
    </div>
  `;
}

function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

// ── Finalization Guard ─────────────────────────────────────────────

function assertFinalized(report: ReportResponse): void {
  if (report.status !== 'final') {
    console.warn('EXPORT_BLOCKED_NOT_FINAL | report_id=%d status=%s', report.id, report.status);
    throw new Error('Only finalized reports can be exported.');
  }
}

// ── Public API ─────────────────────────────────────────────────────

/**
 * Generate and download a PDF of the finalized report using jsPDF.
 * Deterministic text-based rendering — no html2canvas or DOM cloning.
 */
export async function exportReportPDF(
  report: ReportResponse,
  options: ExportOptions = {},
): Promise<void> {
  console.log('PDF_EXPORT_STARTED | report_id=%d', report.id);

  assertFinalized(report);

  const branding = options.branding || getClinicBranding();
  const sections = parseReportSections(report.report_text);

  console.log('PDF_EXPORT_BUILDING | sections=%d', sections.length);

  const doc = buildPdfDocument(report, sections, branding, options.radiologistName, options);

  const filename =
    options.filename || `report_${report.id}_${report.session_id}_finalized.pdf`;

  doc.save(filename);

  console.log('PDF_EXPORT_COMPLETED | report_id=%d filename=%s pages=%d',
    report.id, filename, doc.getNumberOfPages());
}

/**
 * Open the browser's print dialog with a print-optimized report layout.
 */
export function printReport(
  report: ReportResponse,
  options: ExportOptions = {},
): void {
  assertFinalized(report);

  console.log('PRINT_TRIGGERED | report_id=%d', report.id);

  const branding = options.branding || getClinicBranding();
  const sections = parseReportSections(report.report_text);
  const html = buildExportHTML(report, sections, branding, options.radiologistName, options);

  const printWindow = window.open('', '_blank', 'width=900,height=700');
  if (!printWindow) {
    console.error('PRINT_FAILED | popup blocked');
    throw new Error('Popup blocked. Please allow popups for this site.');
  }

  printWindow.document.write(`
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="utf-8" />
      <title>Radiology Report — ${report.id}</title>
      <style>
        @page {
          size: A4;
          margin: 18mm 16mm;
        }
        body {
          margin: 0;
          padding: 20px;
          font-family: 'Inter', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
          color: #222;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .report-section {
          page-break-inside: avoid;
        }
        @media print {
          body { padding: 0; }
        }
      </style>
    </head>
    <body>
      ${html}
    </body>
    </html>
  `);

  printWindow.document.close();

  // Wait for content to render before printing
  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
    console.log('PRINT_LAYOUT_APPLIED | report_id=%d', report.id);
  };
}

/**
 * Download report as plain text file.
 */
export function downloadReportText(report: ReportResponse): void {
  assertFinalized(report);

  const blob = new Blob([report.report_text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `report_${report.id}_${report.session_id}_finalized.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  console.log('TEXT_EXPORT_COMPLETED | report_id=%d', report.id);
}
